import { useTranslation } from 'react-i18next';
import { Stack, Typography } from '@mui/material';

import { IDefaultPaymentMethod } from '@mopla/data-models';
import {
	EditIconV2,
	EMlpIconTypes,
	EMplVariantTypes,
	MplButton,
	MplCardListItem,
	MplIconMessage,
} from '@mopla/ui';

import { EditPaymentMethodSkeleton } from './EditPaymentMethodSkeleton';
import { getPaymentMethodDisplayInfo } from './utils';

interface IEditPaymentMethodOverviewProps {
	isLoading: boolean;
	defaultPaymentMethod: IDefaultPaymentMethod | null;
	isPendingSubscription: boolean;
	onEdit: () => void;
	onAddPaymentMethod: () => void;
}

export const EditPaymentMethodOverview = ({
	isLoading,
	defaultPaymentMethod,
	isPendingSubscription,
	onEdit,
	onAddPaymentMethod,
}: IEditPaymentMethodOverviewProps) => {
	const { t } = useTranslation('editPaymentMethod');

	if (isLoading) {
		return <EditPaymentMethodSkeleton />;
	}

	const displayInfo = defaultPaymentMethod
		? getPaymentMethodDisplayInfo(defaultPaymentMethod, t)
		: null;

	const hasPaymentMethod = defaultPaymentMethod && displayInfo;
	const isSepa = defaultPaymentMethod?.type === 'sepa_debit';

	return (
		<Stack gap={1.5} data-testid="editPaymentMethod-overview">
			<Typography variant="h4-sb" color="primary.main">
				{t('selected_payment_method')}
			</Typography>

			{hasPaymentMethod ? (
				<Stack gap={2}>
					<MplCardListItem
						testId="editPaymentMethod-selectedMethod"
						text={displayInfo.title}
						subtext={displayInfo.subtitle}
						leftIcon={displayInfo.icon}
						rightIcon={<EditIconV2 size={18} />}
						onClick={onEdit}
						disabled={isPendingSubscription}
					/>

					{isSepa && (
						<MplIconMessage
							type={EMlpIconTypes.Warning}
							variant={EMplVariantTypes.Warning}
							message={t('sepa_warning')}
						/>
					)}
				</Stack>
			) : (
				<Stack gap={2}>
					<Typography variant="b2" color="primary.main">
						{t('no_payment_method')}
					</Typography>
					<MplButton
						testId="EditPaymentMethod-AddButton"
						fullWidth
						onClick={onAddPaymentMethod}
					>
						{t('add_payment_method')}
					</MplButton>
				</Stack>
			)}
		</Stack>
	);
};
